import React from 'react';
import { Check, ChevronLeft, Upload } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import { useShare } from 'app/components/header/hooks/use-share';
import { useIntl } from 'react-intl';

interface HeaderProps {
  onBackClick?: () => void;
  backArrowMobileHeaderRef?: React.MutableRefObject<HTMLDivElement>;
  className?: string;
}

export const Header = ({ backArrowMobileHeaderRef, onBackClick, className }: HeaderProps): JSX.Element => {
  const { formatMessage } = useIntl();
   const { share, showCopiedText } = useShare();

  return (
    <div className={cn('absolute top-0 left-0 w-full h-16 z-3 pt-5 pb-1 px-4 bg-transparent flex flex-row items-center justify-between', !onBackClick && 'justify-end', className)} data-testid="back-button-mobile-header" ref={backArrowMobileHeaderRef}>
      {onBackClick && (
        <Button
          variant="ghost"
          size="icon"
          data-testid="back-button"
          aria-label={formatMessage({ id: 'buttons.back' })}
          className="justify-start hover:bg-white/80 rounded-full bg-white opacity-95 text-black h-10 w-10 p-2"
          onClick={onBackClick}
        >
          <ChevronLeft data-testid="chevron-left" className="!h-6 !w-6" aria-hidden="true" />
        </Button>
      )}
      {/* The button stays mounted while "Copied!" shows, so a screen reader's focus does not drop to the document when the state flips. */}
      <Button
        variant="ghost"
        size="icon"
        data-testid="upload-button"
        aria-label={formatMessage({ id: showCopiedText ? 'buttons.copied' : 'buttons.share' })}
        className={cn(
          'justify-start hover:bg-white/80 rounded-full bg-white opacity-95 text-black h-10 px-2.5',
          showCopiedText ? 'w-auto gap-2' : 'w-10',
        )}
        onClick={share}
      >
        {showCopiedText ? (
          <>
            <Check data-testid="check-icon" className="!h-5 !w-5" color="green" aria-hidden="true" />
            <span className="text-md text-system-grey-dark" aria-hidden="true">{formatMessage({ id: 'buttons.copied' })}</span>
          </>
        ) : (
          <Upload data-testid="upload-icon" className="!h-5 !w-5" aria-hidden="true" />
        )}
      </Button>
      {/* Always mounted, because a live region only announces changes made after it is in the tree. */}
      <span className="sr-only" role="status" aria-live="polite" data-testid="copied-status">
        {showCopiedText ? formatMessage({ id: 'buttons.copied' }) : ''}
      </span>
    </div>
  );
};
